import React, { useState, useMemo, useEffect, useContext } from 'react';
import axiosInstance from './axiosInstance';
import CompanySelector from './components/CompanySelector';
import StoreUptimeModal from './StoreUptimeModal';
import GatewayDayModal from './GatewayDayModal';
import './styles/OfflineGatewayAnalysis.css';
import ProtectedComponent from './ProtectedComponent';
import { AuthContext } from './context/AuthContext';

function OfflineGatewayAnalysis() {
  // Auth context
  const { user } = useContext(AuthContext);

  // Form inputs
  const [company, setCompany] = useState('');
  const [selectedCompanyObj, setSelectedCompanyObj] = useState(null);

  // Data & UI state
  const [stores, setStores] = useState([]);
  const [gatewayData, setGatewayData] = useState({}); // { storeCode: { gateways: [], details: {} } }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchedAt, setFetchedAt] = useState('');

  // Progress tracking
  const [progress, setProgress] = useState({ current: 0, total: 0, stage: '' });

  // Table sorting
  const [sortField, setSortField] = useState('uptimePercent');
  const [sortDirection, setSortDirection] = useState('asc');

  // Filtering
  const [filterStore, setFilterStore] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, online, offline

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // AI Analysis state
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [aiError, setAiError] = useState('');
  const [selectedStores, setSelectedStores] = useState(new Set());

  // Expanded rows
  const [expandedStores, setExpandedStores] = useState(new Set());
  const [expandedStoreData, setExpandedStoreData] = useState({}); // { storeId: { loading, error, dailyGridData } }

  // Uptime refresh state
  const [refreshingUptime, setRefreshingUptime] = useState(false);

  // Offline gateways modal
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [offlineGateways, setOfflineGateways] = useState([]);
  const [downtimeTimeline, setDowntimeTimeline] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [expandedDays, setExpandedDays] = useState(new Set());

  // Mode selection: 'analysis' (offline troubleshooting), 'rebate' (monthly uptime reporting), or 'firmware' (firmware breakdown)
  const [mode, setMode] = useState('rebate'); // 'rebate', 'analysis', or 'firmware' - defaults to rebate (Uptime Reporting)

  // Email configuration modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);
  const [loadingEmailConfig, setLoadingEmailConfig] = useState(false);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientName, setNewRecipientName] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Date range filter state - simplified to just month selection
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // Default to current month for rebate mode

  // Cache for fetched month data - key: "company|mode|month", value: { stores, gatewayData, fetchedAt }
  const [monthCache, setMonthCache] = useState({});

  // Calculate startDate and endDate based on selectedMonth
  const getDateRangeFromMonth = () => {
    const now = new Date();

    // Helper to format date as YYYY-MM-DDTHH:MM in local time (no timezone conversion)
    const formatLocalDateTime = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    if (selectedMonth === 'live') {
      // Last 24 hours
      const end = now;
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return {
        startDate: formatLocalDateTime(start),
        endDate: formatLocalDateTime(end)
      };
    } else {
      // Full month: YYYY-MM format
      const [year, month] = selectedMonth.split('-').map(Number);
      const start = new Date(year, month - 1, 1, 0, 0, 0); // First day of month at midnight
      const end = new Date(year, month, 0, 23, 59, 59); // Last day of month at 23:59:59
      return {
        startDate: formatLocalDateTime(start),
        endDate: formatLocalDateTime(end)
      };
    }
  };

  const { startDate, endDate } = getDateRangeFromMonth();

  // Gateway drill-down modal state
  const [drillDownGateway, setDrillDownGateway] = useState(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownError, setDrillDownError] = useState('');

  // Store uptime modal state
  const [storeUptimeModal, setStoreUptimeModal] = useState(null);

  // Gateway-Day drill-down modal state (for clicking a specific day cell)
  const [gatewayDayModal, setGatewayDayModal] = useState(null); // { company, store, gateway, gatewayName, day }

  // Fetch stores and gateway data (hybrid: SaaS for details, MongoDB for uptime)
  const handleFetch = async () => {
    if (!company.trim()) {
      setError('Please enter Company Code');
      return;
    }

    setLoading(true);
    setError('');
    setStores([]);
    setGatewayData({});
    setFetchedAt('');
    setExpandedStores(new Set()); // Close all expanded stores
    setExpandedStoreData({}); // Clear cached daily grid data when fetching new date range
    setProgress({ current: 0, total: 0, stage: '' });

    try {
      const token = document.cookie.split('; ').find(row => row.startsWith('jwt='))?.split('=')[1];

      // For Uptime Reporting mode, fetch directly from MongoDB (no SaaS API calls)
      if (mode === 'rebate') {
        setProgress({ current: 0, total: 1, stage: 'Loading uptime data from database...' });

        const params = new URLSearchParams({ company: company.trim() });
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const uptimeResponse = await fetch(`/api/gateway-uptime/company-summary?${params}`, {
          method: 'GET',
          credentials: 'include'
        });

        if (!uptimeResponse.ok) {
          throw new Error('Failed to fetch uptime data from database');
        }

        const uptimeData = await uptimeResponse.json();
        console.log('[OfflineGatewayAnalysis] MongoDB uptime data:', uptimeData);

        // Store actual days count for display
        window.actualDaysCount = uptimeData.actualDays || 0;
        window.overallHoursOnline = uptimeData.overallStats?.hoursOnline || 0;
        window.overallHoursOffline = uptimeData.overallStats?.hoursOffline || 0;

        // Build store list from MongoDB data
        const storeList = uptimeData.stores.map(s => ({
          store: s.storeId,
          storeName: s.storeName || s.storeId,
          hoursOnline: s.hoursOnline,
          hoursOffline: s.hoursOffline
        }));
        setStores(storeList);

        // Check if no data was found
        if (storeList.length === 0 || uptimeData.totalGateways === 0) {
          setError(`No uptime data found for company "${company}". This company may not have any stores with gateway uptime history in the database.`);
          setLoading(false);
          setProgress({ current: 0, total: 0, stage: '' });
          return;
        }

        // Build gateway data structure from MongoDB results
        const gwData = {};
        uptimeData.stores.forEach(store => {
          gwData[store.storeId] = {
            gateways: store.gateways.map(gw => ({
              deviceId: gw.gwId,
              macAddress: gw.gwId,
              name: gw.gwName || gw.gwId,
              networkStatus: gw.currentStatus === 'online' ? 'CONNECTED' : 'DISCONNECTED',
              uptimePercent: gw.uptimePercent || 0
            })),
            storeName: store.storeName,
            storeData: { store: store.storeId, storeName: store.storeName }
          };
        });

        setGatewayData(gwData);
        setFetchedAt(uptimeData.lastUpdated || new Date().toISOString());
        setLoading(false);
        setProgress({ current: 0, total: 0, stage: '' });
        return;
      }

      // Analysis/Firmware mode: Fetch from SaaS API
      // Step 1: Fetch store list
      setProgress({ current: 0, total: 1, stage: 'Fetching store list...' });

      const storeResponse = await fetch('/api/offline-gateways/stores', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        body: JSON.stringify({ company: company.trim() })
      });

      if (!storeResponse.ok) {
        const errorData = await storeResponse.json();
        throw new Error(errorData.error || 'Failed to fetch stores');
      }

      const storeData = await storeResponse.json();
      const storeList = storeData.stores || [];
      setStores(storeList);

      if (storeList.length === 0) {
        setError(`No stores found for company "${company}". This company may not have any configured stores in the system.`);
        setLoading(false);
        setProgress({ current: 0, total: 0, stage: '' });
        return;
      }

      // Step 2: Fetch gateway data from SaaS for each store (in parallel batches)
      setProgress({ current: 0, total: storeList.length, stage: 'Fetching gateway data...' });

      const gwData = {};
      const BATCH_SIZE = 25;

      for (let i = 0; i < storeList.length; i += BATCH_SIZE) {
        const batch = storeList.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (store) => {
          try {
            const gwResponse = await fetch('/api/offline-gateways/gateways', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
              },
              credentials: 'include',
              body: JSON.stringify({
                company: company.trim(),
                store: store.store
              })
            });

            if (gwResponse.ok) {
              const gwResult = await gwResponse.json();
              return {
                storeCode: store.store,
                data: {
                  gateways: gwResult.gateways || [],
                  storeName: store.storeName,
                  storeData: store
                }
              };
            }
          } catch (err) {
            console.error(`Error fetching gateways for store ${store.store}:`, err);
          }
          return null;
        });

        const batchResults = await Promise.all(batchPromises);

        batchResults.forEach(result => {
          if (result) {
            gwData[result.storeCode] = result.data;
          }
        });

        setProgress({
          current: Math.min(i + BATCH_SIZE, storeList.length),
          total: storeList.length,
          stage: `Fetching gateways... ${Math.min(i + BATCH_SIZE, storeList.length)}/${storeList.length}`
        });
      }

      // Step 3: Fetch uptime data - use live API for current data, MongoDB for historical
      // Skip uptime fetch for firmware mode - we only need gateway list with version info
      let lastUpdatedTimestamp = new Date().toISOString(); // Default timestamp

      if (mode !== 'firmware') {
        const isLiveOrCurrentMonth = selectedMonth === 'live' || selectedMonth === new Date().toISOString().slice(0, 7);

        if (isLiveOrCurrentMonth) {
          setProgress({ current: 0, total: 1, stage: 'Fetching live uptime data from SaaS API...' });
        } else {
          setProgress({ current: 0, total: 1, stage: 'Loading historical uptime data from database...' });
        }

        let uptimeResponse;

        if (isLiveOrCurrentMonth) {
          // Call live SaaS API
          uptimeResponse = await fetch('/api/offline-gateways/company-summary-live', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              company: company.trim(),
              startDate: startDate,
              endDate: endDate
            })
          });
        } else {
          // Call MongoDB historical data
          const params = new URLSearchParams({ company: company.trim() });
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);

          uptimeResponse = await fetch(`/api/gateway-uptime/company-summary?${params}`, {
            method: 'GET',
            credentials: 'include'
          });
        }

        if (uptimeResponse.ok) {
          const uptimeData = await uptimeResponse.json();
          lastUpdatedTimestamp = uptimeData.lastUpdated || lastUpdatedTimestamp;
          console.log('[OfflineGatewayAnalysis] Uptime data received:', {
            totalGateways: uptimeData.totalGateways,
            stores: uptimeData.stores?.length,
            overallStats: uptimeData.overallStats
          });

          // Merge uptime data into gateway objects
          let mergedCount = 0;
          let unmatchedCount = 0;
          uptimeData.stores?.forEach(uptimeStore => {
            const storeGwData = gwData[uptimeStore.storeId];
            if (storeGwData) {
              console.log(`[Merge] Store ${uptimeStore.storeId}: ${uptimeStore.gateways.length} uptime records, ${storeGwData.gateways.length} SaaS gateways`);
              uptimeStore.gateways.forEach(uptimeGw => {
                // Strip company suffix from MongoDB gwId for matching (e.g., "D02544FFFE20C5FBBPS" -> "D02544FFFE20C5FB")
                const cleanGwId = uptimeGw.gwId.replace(/(?:BPS|WFC|WLAB|TEST)$/, '');

                const gwIndex = storeGwData.gateways.findIndex(
                  gw => gw.deviceId === cleanGwId ||
                        gw.macAddress === cleanGwId ||
                        gw.deviceId === uptimeGw.gwId ||
                        gw.macAddress === uptimeGw.gwId
                );
                if (gwIndex >= 0) {
                  storeGwData.gateways[gwIndex].uptimePercent = uptimeGw.uptimePercent || 0;
                  mergedCount++;
                } else {
                  unmatchedCount++;
                  console.log(`[Merge] UNMATCHED: ${uptimeGw.gwId} (clean: ${cleanGwId})`);
                }
              });
            }
          });

          console.log(`[OfflineGatewayAnalysis] Merged uptime data for ${mergedCount} gateways, ${unmatchedCount} unmatched`);

          // Show warning if no uptime data was found
          if (mergedCount === 0 && uptimeData.totalGateways === 0) {
            setError('⚠️ No uptime history found in database. Click "Refresh Uptime Data" to collect uptime data for this company.');
            setFetchedAt(''); // Clear timestamp when no data
          } else {
            // Only set timestamp if we actually have uptime data
            setFetchedAt(uptimeData.lastUpdated || new Date().toISOString());
          }
        } else {
          console.warn('[OfflineGatewayAnalysis] Failed to fetch uptime data:', uptimeResponse.status, uptimeResponse.statusText);
          setError('⚠️ Failed to load uptime history. Click "Refresh Uptime Data" to collect uptime data.');
          setFetchedAt(''); // Clear timestamp on failure
        }
      } // End of if (mode !== 'firmware')

      setGatewayData(gwData);

      // Cache the fetched data
      const cacheKey = `${company}|${mode}|${selectedMonth}`;
      setMonthCache(prev => ({
        ...prev,
        [cacheKey]: {
          stores: storeList,
          gatewayData: gwData,
          fetchedAt: lastUpdatedTimestamp
        }
      }));

    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to fetch gateway data. Please check your credentials and try again.');
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0, stage: '' });
    }
  };

  // Toggle store expansion
  const toggleStoreExpansion = async (storeCode) => {
    const isExpanding = !expandedStores.has(storeCode);

    setExpandedStores(prev => {
      const newSet = new Set(prev);
      if (newSet.has(storeCode)) {
        newSet.delete(storeCode);
      } else {
        newSet.add(storeCode);
      }
      return newSet;
    });

    // If expanding in rebate mode, fetch daily grid data using pre-calculated summaries
    if (isExpanding && mode === 'rebate' && !expandedStoreData[storeCode]) {
      setExpandedStoreData(prev => ({
        ...prev,
        [storeCode]: { loading: true, error: null, dailyGridData: null }
      }));

      try {
        // Use the FAST endpoint with pre-calculated daily summaries
        const response = await fetch(
          `/api/gateway-uptime/store-daily-grid-fast?company=${encodeURIComponent(company)}&store=${encodeURIComponent(storeCode)}&yearMonth=${encodeURIComponent(selectedMonth)}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch daily grid data');
        }

        const data = await response.json();

        setExpandedStoreData(prev => ({
          ...prev,
          [storeCode]: { loading: false, error: null, dailyGridData: data }
        }));
      } catch (err) {
        console.error('Error fetching daily grid:', err);
        setExpandedStoreData(prev => ({
          ...prev,
          [storeCode]: { loading: false, error: err.message, dailyGridData: null }
        }));
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Calculate time range label for uptime stats
  const getUptimeTimeRangeLabel = () => {
    // Use actual days count from API if available (more accurate than date range calculation)
    if (window.actualDaysCount && window.actualDaysCount > 0) {
      return `${window.actualDaysCount} day${window.actualDaysCount > 1 ? 's' : ''}`;
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffMs = end - start;
      const diffHours = Math.round(diffMs / (1000 * 60 * 60));
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays >= 1) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
      } else {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
      }
    } else if (startDate || endDate) {
      return 'custom range';
    } else {
      // Default: show most recent snapshot data (snapshots taken every 6 hours)
      return 'latest snapshot';
    }
  };

  // Process data for display
  const processedData = useMemo(() => {
    let data = stores.map(store => {
      const gwInfo = gatewayData[store.store];
      const gateways = gwInfo?.gateways || [];
      const offlineGateways = gateways.filter(gw => gw.networkStatus === 'DISCONNECTED');

      // Calculate uptime percentage
      // Method 1: Use hours data if available (from backend API - most accurate)
      // Method 2: Fall back to averaging gateway uptimePercent values (for Analysis mode)
      let totalUptime = 0;
      const totalHours = (store.hoursOnline || 0) + (store.hoursOffline || 0);

      if (totalHours > 0) {
        // Use hours-based calculation (Rebate mode with MongoDB data)
        totalUptime = (store.hoursOnline / totalHours) * 100;
      } else if (gateways.length > 0) {
        // Fall back to averaging gateway uptime percentages (Analysis mode)
        // Check if any gateway has uptimePercent defined (number type)
        const gatewaysWithUptime = gateways.filter(gw => typeof gw.uptimePercent === 'number');
        if (gatewaysWithUptime.length > 0) {
          totalUptime = gatewaysWithUptime.reduce((sum, gw) => sum + gw.uptimePercent, 0) / gatewaysWithUptime.length;
        }
      }

      return {
        ...store,
        totalGateways: gateways.length,
        offlineGateways: offlineGateways.length,
        onlineGateways: gateways.length - offlineGateways.length,
        uptimePercent: Math.round(totalUptime * 1000) / 1000, // Round to 3 decimals for precision
        gateways: gateways,
        gatewayData: gwInfo
      };
    });

    // Apply filters
    if (filterStore) {
      data = data.filter(item => {
        const storeName = item.storeName || '';
        const storeCode = item.store || '';
        return storeName.toLowerCase().includes(filterStore.toLowerCase()) ||
               storeCode.toLowerCase().includes(filterStore.toLowerCase());
      });
    }

    if (filterStatus !== 'all') {
      data = data.filter(item => {
        if (filterStatus === 'offline') {
          return item.offlineGateways > 0;
        } else if (filterStatus === 'online') {
          return item.offlineGateways === 0 && item.totalGateways > 0;
        }
        return true;
      });
    }

    // Sort
    data.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'store':
          aVal = a.store || '';
          bVal = b.store || '';
          break;
        case 'storeName':
          aVal = a.storeName || '';
          bVal = b.storeName || '';
          break;
        case 'totalGateways':
          aVal = a.totalGateways || 0;
          bVal = b.totalGateways || 0;
          break;
        case 'offlineGateways':
          aVal = a.offlineGateways || 0;
          bVal = b.offlineGateways || 0;
          break;
        case 'onlineGateways':
          aVal = a.onlineGateways || 0;
          bVal = b.onlineGateways || 0;
          break;
        case 'uptimePercent':
          aVal = a.uptimePercent || 0;
          bVal = b.uptimePercent || 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [stores, gatewayData, sortField, sortDirection, filterStore, filterStatus]);

  // Firmware summary - calculate gateway counts by firmware version
  const firmwareSummary = useMemo(() => {
    if (mode !== 'firmware') return { versions: {}, total: 0 };

    const versionCounts = {};
    let totalCount = 0;

    processedData.forEach(store => {
      const gateways = store.gateways || [];
      gateways.forEach(gw => {
        const version = gw.version || 'Unknown';
        if (!versionCounts[version]) {
          versionCounts[version] = 0;
        }
        versionCounts[version]++;
        totalCount++;
      });
    });

    // Sort versions - put 'Unknown' at the end
    const sortedVersions = Object.entries(versionCounts)
      .sort((a, b) => {
        if (a[0] === 'Unknown') return 1;
        if (b[0] === 'Unknown') return -1;
        return b[1] - a[1]; // Sort by count descending
      });

    return {
      versions: Object.fromEntries(sortedVersions),
      sortedVersions: sortedVersions,
      total: totalCount
    };
  }, [mode, processedData]);

  // Calculate store firmware breakdown for expanded view
  const getStoreFirmwareBreakdown = (storeCode) => {
    const gwInfo = gatewayData[storeCode];
    const gateways = gwInfo?.gateways || [];

    const versionGroups = {};
    gateways.forEach(gw => {
      const version = gw.version || 'Unknown';
      if (!versionGroups[version]) {
        versionGroups[version] = [];
      }
      versionGroups[version].push(gw);
    });

    // Sort versions - put 'Unknown' at the end
    return Object.entries(versionGroups)
      .sort((a, b) => {
        if (a[0] === 'Unknown') return 1;
        if (b[0] === 'Unknown') return -1;
        return b[1].length - a[1].length; // Sort by count descending
      });
  };

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return processedData.slice(startIndex, endIndex);
  }, [processedData, currentPage, pageSize]);

  const totalPages = Math.ceil(processedData.length / pageSize);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStore, filterStatus]);

  // Pagination handlers
  const goToPage = (page) => {
    const targetPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(targetPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (processedData.length === 0) {
      alert('No data to export');
      return;
    }

    let headers, rows, filename;

    if (mode === 'rebate') {
      // Uptime Reporting (Rebates) mode - export uptime data
      headers = ['Store Code', 'Store Name', 'Total Gateways', 'Uptime %', 'Online Hours', 'Total Hours'];

      rows = processedData.map(store => {
        const onlineHours = store.hoursOnline || 0;
        const offlineHours = store.hoursOffline || 0;
        const totalHours = onlineHours + offlineHours;

        return [
          store.store,
          store.storeName || '',
          store.totalGateways || 0,
          store.uptimePercent ? store.uptimePercent.toFixed(2) : '0.00',
          onlineHours.toFixed(1),
          totalHours.toFixed(1)
        ];
      });

      filename = `uptime_report_${company}_${selectedMonth}_${new Date().toISOString().split('T')[0]}.csv`;
    } else if (mode === 'firmware') {
      // Firmware mode - export gateway firmware breakdown
      headers = ['Store Code', 'Store Name', 'Firmware Version', 'Gateway Count', 'Gateway Name', 'MAC Address', 'Status', 'Model'];

      rows = [];
      processedData.forEach(store => {
        const firmwareBreakdown = getStoreFirmwareBreakdown(store.store);
        if (firmwareBreakdown.length === 0) {
          rows.push([store.store, store.storeName, '', 0, '', '', '', '']);
        } else {
          firmwareBreakdown.forEach(([version, gateways]) => {
            gateways.forEach((gw, idx) => {
              rows.push([
                store.store,
                store.storeName,
                version,
                idx === 0 ? gateways.length : '', // Only show count on first row of each version group
                gw.name || '',
                gw.macAddress || '',
                gw.networkStatus || '',
                gw.model || ''
              ]);
            });
          });
        }
      });

      filename = `firmware_report_${company}_${new Date().toISOString().split('T')[0]}.csv`;
    } else {
      // Analysis mode - export gateway details
      headers = ['Store Code', 'Store Name', 'Total Gateways', 'Online', 'Offline', 'Gateway Name', 'MAC Address', 'Status', 'Version', 'Last Connection', 'Last Reboot', 'Label Count'];

      rows = [];
      processedData.forEach(store => {
        const gateways = store.gateways || [];
        if (gateways.length === 0) {
          rows.push([store.store, store.storeName, 0, 0, 0, '', '', '', '', '', '', '']);
        } else {
          gateways.forEach(gw => {
            rows.push([
              store.store,
              store.storeName,
              store.totalGateways,
              store.onlineGateways,
              store.offlineGateways,
              gw.name || '',
              gw.macAddress || '',
              gw.networkStatus || '',
              gw.version || '',
              gw.lastConnectionDate || '',
              gw.lastRebootTime || '',
              gw.labelCount || ''
            ]);
          });
        }
      });

      filename = `offline_gateways_${company}_${new Date().toISOString().split('T')[0]}.csv`;
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Toggle store selection
  const toggleStoreSelection = (storeCode) => {
    setSelectedStores(prev => {
      const newSet = new Set(prev);
      if (newSet.has(storeCode)) {
        newSet.delete(storeCode);
      } else {
        newSet.add(storeCode);
      }
      return newSet;
    });
  };

  // Select all/none stores
  const toggleAllStores = () => {
    if (selectedStores.size === processedData.length) {
      setSelectedStores(new Set());
    } else {
      setSelectedStores(new Set(processedData.map(s => s.store)));
    }
  };

  // Refresh uptime data handler
  const handleRefreshUptimeData = async () => {
    if (!company.trim()) {
      alert('Please select a company first');
      return;
    }

    setRefreshingUptime(true);
    setError('');

    try {
      const token = document.cookie.split('; ').find(row => row.startsWith('jwt='))?.split('=')[1];

      const response = await fetch('/api/offline-gateways/trigger-uptime-job', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        body: JSON.stringify({ company: company.trim() })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to trigger uptime refresh';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseErr) {
          // If response is not JSON (e.g., HTML error page), use status text
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      alert(`Uptime data refresh initiated for company ${company}.\n\nProcessed:\n- ${result.stats.companiesProcessed} companies\n- ${result.stats.storesProcessed} stores\n- ${result.stats.gatewaysProcessed} gateways\n\nDuration: ${result.stats.durationMinutes} minutes`);

      // Auto-refetch data after uptime refresh completes
      console.log('[OfflineGatewayAnalysis] Auto-refetching data after uptime refresh...');
      await handleFetch();

    } catch (err) {
      console.error('Uptime refresh error:', err);
      setError(err.message || 'Failed to refresh uptime data. Please try again.');
    } finally {
      setRefreshingUptime(false);
    }
  };

  // Gateway drill-down handler
  const handleGatewayDrillDown = async (gateway) => {
    setDrillDownGateway(gateway);
    setDrillDownLoading(true);
    setDrillDownData(null);
    setDrillDownError('');

    try {
      const token = document.cookie.split('; ').find(row => row.startsWith('jwt='))?.split('=')[1];

      // Build query params
      const params = new URLSearchParams({
        company: company.trim(),
        gwId: gateway.deviceId || gateway.macAddress
      });

      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      // Fetch summary data
      const summaryResponse = await fetch(`/api/gateway-uptime/summary?${params.toString()}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include'
      });

      if (!summaryResponse.ok) {
        throw new Error('Failed to fetch gateway uptime data');
      }

      const summaryData = await summaryResponse.json();

      // Fetch intervals data (limit to most recent for performance)
      const intervalsResponse = await fetch(`/api/gateway-uptime/intervals?${params.toString()}&limit=10`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include'
      });

      let intervalsData = null;
      if (intervalsResponse.ok) {
        intervalsData = await intervalsResponse.json();
      }

      setDrillDownData({
        summary: summaryData,
        intervals: intervalsData
      });

    } catch (err) {
      console.error('Drill-down error:', err);
      setDrillDownError(err.message || 'Failed to load gateway details');
    } finally {
      setDrillDownLoading(false);
    }
  };

  // AI Analysis handler
  const handleAiAnalysis = async () => {
    if (processedData.length === 0) {
      alert('No data to analyze');
      return;
    }

    // Get stores to analyze (selected or all)
    const storesToAnalyze = selectedStores.size > 0
      ? processedData.filter(store => selectedStores.has(store.store))
      : processedData;

    if (storesToAnalyze.length === 0) {
      alert('Please select at least one store to analyze');
      return;
    }

    setShowAiModal(true);
    setAiAnalyzing(true);
    setAiResult('');
    setAiError('');

    try {
      // Collect offline gateways from selected stores
      const offlineGateways = [];
      storesToAnalyze.forEach(store => {
        const gateways = store.gateways || [];
        gateways.forEach(gw => {
          if (gw.networkStatus === 'DISCONNECTED') {
            offlineGateways.push({
              store: store.store,
              storeName: store.storeName,
              gatewayName: gw.name,
              macAddress: gw.macAddress,
              deviceId: gw.deviceId || gw.ipAddress,
              version: gw.version,
              lastConnectionDate: gw.lastConnectionDate,
              lastRebootTime: gw.lastRebootTime,
              labelCount: gw.labelCount,
              dataChannel: gw.dataChannel,
              model: gw.model
            });
          }
        });
      });

      const token = document.cookie.split('; ').find(row => row.startsWith('jwt='))?.split('=')[1];

      // Get actual company name from the selected company
      const companyName = selectedCompanyObj?.companyName || company;

      const response = await fetch('/api/offline-gateways/ai-analysis-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        body: JSON.stringify({
          sampleData: offlineGateways,
          question: 'Analyze these offline gateways and identify patterns, root causes, and provide actionable recommendations for getting them back online.',
          context: {
            companyCode: company,
            companyName: companyName,
            analyzedStores: storesToAnalyze.length,
            totalStores: stores.length,
            analyzedGateways: storesToAnalyze.reduce((sum, s) => sum + (s.gateways?.length || 0), 0),
            totalGateways: stores.reduce((sum, s) => sum + (gatewayData[s.store]?.gateways?.length || 0), 0),
            offlineGateways: offlineGateways.length
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start AI analysis');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullResult = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.substring(6));

            if (data.type === 'content') {
              fullResult += data.text;
              setAiResult(fullResult);
            } else if (data.type === 'complete') {
              setAiResult(data.result);
              console.log(`AI Analysis complete - Cost: $${data.cost?.toFixed(4)}, Duration: ${data.duration_ms}ms`);
            } else if (data.type === 'error') {
              setAiError(data.error);
            }
          }
        }
      }
    } catch (err) {
      console.error('AI Analysis error:', err);
      setAiError(err.message || 'Failed to analyze data');
    } finally {
      setAiAnalyzing(false);
    }
  };

  // Email configuration handlers
  const handleOpenEmailModal = async () => {
    setShowEmailModal(true);
    setLoadingEmailConfig(true);
    try {
      const response = await axiosInstance.get('/uptime-email/config');
      setEmailConfig(response.data);
    } catch (error) {
      console.error('Error loading email config:', error);
      alert('Failed to load email configuration');
    } finally {
      setLoadingEmailConfig(false);
    }
  };

  const handleAddRecipient = async () => {
    if (!newRecipientEmail) {
      alert('Please enter an email address');
      return;
    }

    try {
      const response = await axiosInstance.post('/uptime-email/recipients', {
        email: newRecipientEmail,
        name: newRecipientName || newRecipientEmail
      });
      setEmailConfig(response.data.config);
      setNewRecipientEmail('');
      setNewRecipientName('');
    } catch (error) {
      console.error('Error adding recipient:', error);
      alert(error.response?.data?.error || 'Failed to add recipient');
    }
  };

  const handleRemoveRecipient = async (email) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove ${email} from recipients?`)) return;

    try {
      const response = await axiosInstance.delete(`/uptime-email/recipients/${encodeURIComponent(email)}`);
      setEmailConfig(response.data.config);
    } catch (error) {
      console.error('Error removing recipient:', error);
      alert('Failed to remove recipient');
    }
  };

  const handleToggleEnabled = async () => {
    try {
      const response = await axiosInstance.put('/uptime-email/config', {
        enabled: !emailConfig.enabled
      });
      setEmailConfig(response.data.config);
    } catch (error) {
      console.error('Error updating config:', error);
      alert('Failed to update configuration');
    }
  };

  const handleToggleHtmlReport = async () => {
    try {
      const response = await axiosInstance.put('/uptime-email/config', {
        reportConfig: {
          ...emailConfig.reportConfig,
          includeHtmlReport: !emailConfig.reportConfig?.includeHtmlReport
        }
      });
      setEmailConfig(response.data.config);
    } catch (error) {
      console.error('Error updating HTML report config:', error);
      alert('Failed to update configuration');
    }
  };

  const handleSendTestEmail = async () => {
    if (emailConfig.recipients.length === 0) {
      alert('Please add at least one recipient first');
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Send test email to ${emailConfig.recipients.length} recipient(s)?`)) return;

    setSendingTestEmail(true);
    try {
      await axiosInstance.post('/uptime-email/send-test');
      alert('Test email sent successfully!');
    } catch (error) {
      console.error('Error sending test email:', error);
      alert(error.response?.data?.error || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Generate month options for current + last 11 months
  const generateMonthOptions = () => {
    const months = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      // Current month shows as "This Month", past months show "Month Year"
      const label = (i === 0) ? 'This Month' : date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      months.push({ value: yearMonth, label: label });
    }

    return months;
  };

  // Render inline daily grid for rebate mode expanded stores
  const renderInlineDailyGrid = (storeCode) => {
    const storeData = expandedStoreData[storeCode];

    if (!storeData) return null;

    if (storeData.loading) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
          <div className="spinner" style={{ margin: '0 auto 10px' }}></div>
          Loading daily grid data...
        </div>
      );
    }

    if (storeData.error) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>
          Error: {storeData.error}
        </div>
      );
    }

    if (!storeData.dailyGridData || storeData.dailyGridData.gateways.length === 0) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
          No daily uptime data available
        </div>
      );
    }

    const { days, gateways } = storeData.dailyGridData;

    const formatDayHeader = (dateStr) => {
      // Parse date string without timezone conversion
      // "2026-01-14" should display as "Jan 14", not "Jan 13"
      const [year, month, day] = dateStr.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const getUptimeClass = (percent) => {
      if (percent === null || percent === undefined) return 'no-data';
      if (percent >= 99.1) return 'excellent';  // Green: 99.1% - 100%
      if (percent >= 95) return 'warning';      // Yellow: 95% - 99%
      return 'poor';                            // Red: < 95%
    };

    return (
      <div className="inline-daily-grid" style={{ padding: '16px', maxHeight: '500px', overflow: 'auto' }}>
        <table className="daily-grid-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#243447', zIndex: 5 }}>
            <tr>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2d3748', minWidth: '200px', position: 'sticky', left: 0, background: '#243447', fontSize: '14px' }}>Gateway</th>
              {days.map(day => (
                <th key={day} style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #2d3748', minWidth: '70px', fontSize: '14px' }}>
                  {formatDayHeader(day)}
                </th>
              ))}
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #2d3748', minWidth: '90px', background: '#2a3f5f', fontWeight: 700, fontSize: '14px' }}>Overall</th>
            </tr>
          </thead>
          <tbody>
            {gateways.map((gateway, idx) => {
              // Remove company suffix from gateway ID (only remove known company codes)
              const cleanGwId = gateway.gwId.replace(/(?:BPS|WFC|WLAB|TEST)$/, '');

              return (
                <tr
                  key={idx}
                  style={{ borderBottom: '1px solid #1e293b', transition: 'background 0.2s ease-in-out' }}
                  className={`gateway-row-${idx}`}
                >
                  <td style={{ padding: '10px 12px', position: 'sticky', left: 0, background: '#1a1a2e', zIndex: 3, borderRight: '2px solid #2d3748', transition: 'background 0.2s ease-in-out' }}>
                    <div style={{ fontWeight: 600, color: '#e0e0e0', fontSize: '15px', fontFamily: 'Courier New, monospace' }}>{cleanGwId}</div>
                  </td>
                {days.map(day => {
                  const uptime = gateway.dailyUptime[day];
                  const hasData = uptime !== undefined && uptime !== null;
                  const className = getUptimeClass(uptime);
                  const style = {
                    padding: '12px 8px',
                    textAlign: 'center',
                    borderRight: '1px solid #1e293b',
                    fontWeight: 600,
                    fontSize: '15px',
                    cursor: hasData ? 'pointer' : 'default',
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative'
                  };

                  // Add background colors based on uptime
                  if (className === 'excellent') {
                    style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.25) 100%)';
                    style.color = '#34d399';
                  } else if (className === 'good') {
                    style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.2) 100%)';
                    style.color = '#4ade80';
                  } else if (className === 'warning') {
                    style.background = 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.25) 100%)';
                    style.color = '#fbbf24';
                  } else if (className === 'poor') {
                    style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.25) 100%)';
                    style.color = '#f87171';
                  } else {
                    style.background = '#0a0e14';
                    style.color = '#4b5563';
                    style.fontStyle = 'italic';
                  }

                  return (
                    <td
                      key={day}
                      style={style}
                      title={hasData ? `Click to view ${cleanGwId} on ${day}` : ''}
                      onMouseEnter={(e) => {
                        if (hasData) {
                          // Highlight entire row
                          const row = e.currentTarget.closest('tr');
                          const allCells = row.querySelectorAll('td');
                          allCells.forEach(cell => {
                            cell.style.background = 'rgba(56, 189, 248, 0.15)';
                          });

                          // Swell only this cell
                          e.currentTarget.style.transform = 'scale(1.08)';
                          e.currentTarget.style.zIndex = '10';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(56, 189, 248, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (hasData) {
                          // Remove row highlight and restore all cells
                          const row = e.currentTarget.closest('tr');
                          const allCells = row.querySelectorAll('td');
                          allCells.forEach((cell, cellIdx) => {
                            if (cellIdx === 0) {
                              // First cell (gateway name)
                              cell.style.background = '#1a1a2e';
                            } else {
                              // Data cells - restore based on their uptime class
                              const cellText = cell.textContent.trim();
                              if (cellText === '-') {
                                cell.style.background = '#0a0e14';
                              } else {
                                const cellUptime = parseFloat(cellText);
                                if (cellUptime >= 95) {
                                  cell.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.25) 100%)';
                                } else if (cellUptime >= 80) {
                                  cell.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.2) 100%)';
                                } else if (cellUptime >= 50) {
                                  cell.style.background = 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.25) 100%)';
                                } else {
                                  cell.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.25) 100%)';
                                }
                              }
                            }
                          });

                          // Remove swell from this cell
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.zIndex = '1';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                      onClick={() => {
                        if (hasData) {
                          setGatewayDayModal({
                            company,
                            store: storeCode,
                            gateway: cleanGwId,
                            gatewayName: (gateway.gwName || gateway.gwId).replace(/(?:BPS|WFC|WLAB|TEST)$/, ''),
                            day,
                            uptimePercent: uptime
                          });
                        }
                      }}
                    >
                      {!gateway.serverInsertTime ? 'N/A' : (hasData ? `${uptime}%` : '-')}
                    </td>
                  );
                })}
                <td style={{ padding: '12px', textAlign: 'center', background: '#1e2837', fontSize: '15px', fontWeight: 700, color: !gateway.serverInsertTime ? '#6b7280' : (gateway.overallUptime >= 95 ? '#34d399' : gateway.overallUptime >= 80 ? '#fbbf24' : '#f87171') }}>
                  {!gateway.serverInsertTime ? 'N/A' : `${gateway.overallUptime}%`}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Sort indicator
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span className="sort-indicator">⇅</span>;
    return <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <ProtectedComponent functionCode="SA03" functionName="Offline Gateway Analysis">
      <div className="offline-gateways-admin">
      {/* Header */}
      <header className="offline-gateways-header">
        <div className="header-with-mode">
          <div className="header-text">
            <h1>{mode === 'analysis' ? 'Offline Gateway Analysis' : mode === 'firmware' ? 'Gateways by Firmware' : 'Uptime Reporting (Rebates)'}</h1>
            <p>{mode === 'analysis' ? 'Analyze gateway status and identify offline gateways by company' : 'Monthly uptime reports for rebate calculations'}</p>
          </div>
        </div>
      </header>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Two-Row Controls */}
      <div className="single-line-controls">
        {/* Top Row: Company Input → Action Type → Time Period → Fetch */}
        <div className="controls-top-row">
          <CompanySelector
            onCompanySelect={(companyObj, stores) => {
              setCompany(companyObj.companyCode);
              setSelectedCompanyObj(companyObj);
              // Clear existing data when company changes
              setStores([]);
              setGatewayData({});
              setFetchedAt('');
              setError('');
            }}
            onClear={() => {
              setCompany('');
              setSelectedCompanyObj(null);
              // Clear data when company is cleared
              setStores([]);
              setGatewayData({});
              setFetchedAt('');
              setError('');
            }}
            placeholder="Type company code or name..."
            autoFetchStores={false}
          />

          {/* Mode Selection Dropdown */}
          <select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              // Reset month to default when switching modes
              setSelectedMonth(e.target.value === 'analysis' || e.target.value === 'firmware' ? 'live' : new Date().toISOString().slice(0, 7));
              // Clear data to prevent confusion - user must explicitly fetch for new mode
              setStores([]);
              setGatewayData({});
              setFetchedAt('');
            }}
            className="mode-select"
          >
            <option value="rebate">Uptime Reporting</option>
            <option value="analysis">Analysis</option>
            <option value="firmware">Gateways by Firmware</option>
          </select>

          {/* Time Period Selection Dropdown - Hidden for firmware mode (firmware is always current state) */}
          {mode !== 'firmware' && (
            <select
              value={selectedMonth}
              onChange={(e) => {
                const newMonth = e.target.value;
                const cacheKey = `${company}|${mode}|${newMonth}`;

                // Check if we have cached data for this month
                if (monthCache[cacheKey]) {
                  // Restore cached data
                  setStores(monthCache[cacheKey].stores);
                  setGatewayData(monthCache[cacheKey].gatewayData);
                  setFetchedAt(monthCache[cacheKey].fetchedAt);
                } else {
                  // Clear table when switching to unfetched month
                  setStores([]);
                  setGatewayData({});
                  setFetchedAt('');
                }

                setSelectedMonth(newMonth);
              }}
              className="lookup-period-select"
            >
              {mode === 'analysis' ? (
                // Analysis mode: Show 24hr + monthly history
                <>
                  <option value="live">Live (Last 24 Hours)</option>
                  {generateMonthOptions().map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </>
              ) : (
                // Rebate mode: Show only monthly options from DB
                <>
                  {generateMonthOptions().map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          )}

          <button
            className="fetch-button"
            onClick={handleFetch}
            disabled={loading || !company}
          >
            {loading ? 'Fetching...' : 'Fetch'}
          </button>

          {stores.length > 0 && (
            <button className="export-button-top" onClick={handleExportCSV}>
              Export CSV
            </button>
          )}

          {/* Email Reports Configuration Button - Super Admin Only */}
          {user?.isSuperAdmin && mode === 'rebate' && (
            <button
              className="email-config-btn"
              onClick={handleOpenEmailModal}
              title="Configure Email Reports"
              style={{ marginLeft: 'auto' }}
            >
              📧 Email Reports
            </button>
          )}
        </div>

        {/* Bottom Row: Stats + Filters + Export */}
        {stores.length > 0 && (
          <div className="controls-bottom-row">
            <span className="summary-inline" style={{ color: '#d1d5db', fontWeight: '600' }}>Stores: <strong style={{ color: '#ffffff' }}>{stores.length}</strong></span>
            <span className="summary-inline" style={{ color: '#d1d5db', fontWeight: '600' }}>Total Gateways: <strong style={{ color: '#ffffff' }}>{processedData.reduce((sum, s) => sum + s.totalGateways, 0)}</strong></span>
            {mode === 'analysis' && (
              <>
                <span className="summary-inline online" style={{ color: '#d1d5db', fontWeight: '600' }}>Online: <strong style={{ color: '#ffffff' }}>{processedData.reduce((sum, s) => sum + s.onlineGateways, 0)}</strong></span>
                <span className="summary-inline offline" style={{ color: '#d1d5db', fontWeight: '600' }}>Offline: <strong style={{ color: '#ffffff' }}>{processedData.reduce((sum, s) => sum + s.offlineGateways, 0)}</strong></span>
              </>
            )}
            {/* Only show uptime if we have actual gateway data and not in firmware mode */}
            {mode !== 'firmware' && processedData.reduce((sum, s) => sum + s.totalGateways, 0) > 0 && (
              <span className="summary-inline" style={{ fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#d1d5db', fontWeight: '600' }}>Avg Uptime <span style={{ color: '#ffffff' }}>{getUptimeTimeRangeLabel()}</span>:</span>
                <span style={{ color: '#10b981', fontSize: '18px', fontWeight: '600' }}>
                  {window.overallHoursOnline || 0}h
                </span>
                <span style={{ color: '#ffffff', fontSize: '18px', fontWeight: '500' }}>/</span>
                <span style={{ color: '#60a5fa', fontSize: '18px', fontWeight: '600' }}>
                  {((window.overallHoursOnline || 0) + (window.overallHoursOffline || 0)).toFixed(1)}h
                </span>
                <span style={{ color: '#ffffff', fontSize: '18px', fontWeight: '500' }}>=</span>
                <span style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: (() => {
                    const hoursOnline = window.overallHoursOnline || 0;
                    const hoursOffline = window.overallHoursOffline || 0;
                    const totalHours = hoursOnline + hoursOffline;
                    const avgUptime = totalHours > 0 ? (hoursOnline / totalHours) * 100 : 0;
                    return avgUptime >= 95 ? '#34d399' : avgUptime >= 80 ? '#fbbf24' : '#f87171';
                  })()
                }}>
                  {(() => {
                    const hoursOnline = window.overallHoursOnline || 0;
                    const hoursOffline = window.overallHoursOffline || 0;
                    const totalHours = hoursOnline + hoursOffline;
                    const avgUptime = totalHours > 0 ? (hoursOnline / totalHours) * 100 : 0;
                    return avgUptime === 100 ? '100%' : `${avgUptime.toFixed(3)}%`;
                  })()}
                </span>
                <span
                  style={{ color: '#ef4444', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline', marginLeft: '8px' }}
                  onClick={async () => {
                    setShowOfflineModal(true);
                    setLoadingTimeline(true);
                    setDowntimeTimeline(null);

                    try {
                      // Fetch downtime timeline data from backend
                      const params = new URLSearchParams({ company: company.trim() });
                      if (startDate) params.append('startDate', startDate);
                      if (endDate) params.append('endDate', endDate);

                      const response = await fetch(`/api/gateway-uptime/downtime-timeline?${params}`, {
                        credentials: 'include'
                      });

                      if (!response.ok) {
                        throw new Error('Failed to fetch downtime timeline');
                      }

                      const data = await response.json();
                      setDowntimeTimeline(data);
                    } catch (err) {
                      console.error('Error fetching downtime timeline:', err);
                      setDowntimeTimeline({ error: err.message, timeline: [] });
                    } finally {
                      setLoadingTimeline(false);
                    }
                  }}
                  title="Click to view downtime timeline"
                >
                  View downtime ({window.overallHoursOffline || 0}h)
                </span>
              </span>
            )}
            {selectedStores.size > 0 && (
              <span className="summary-inline selected" style={{ color: '#d1d5db', fontWeight: '600' }}>Selected: <strong style={{ color: '#ffffff' }}>{selectedStores.size}</strong></span>
            )}
            {fetchedAt && (
              <span className="summary-inline data-timestamp" title="Last uptime data refresh time" style={{ color: '#d1d5db', fontWeight: '600' }}>
                GW data as of: <strong style={{ color: '#ffffff' }}>{new Date(fetchedAt).toLocaleString()}</strong>
              </span>
            )}
            <div className="divider"></div>
            <input
              type="text"
              placeholder="Filter stores..."
              value={filterStore}
              onChange={(e) => setFilterStore(e.target.value)}
              className="filter-input-inline"
            />
            {mode === 'analysis' && (
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select-inline"
              >
                <option value="all">All</option>
                <option value="offline">W/ Offline</option>
                <option value="online">All Online</option>
              </select>
            )}
            {mode === 'analysis' && (
              <button
                className="ai-analysis-button"
                onClick={handleAiAnalysis}
                disabled={selectedStores.size === 0}
                title={selectedStores.size === 0 ? "Select at least one store to analyze" : ""}
              >
                AI Analysis {selectedStores.size > 0 && `(${selectedStores.size})`}
              </button>
            )}
          </div>
        )}

        {/* Firmware Summary Bar - only show in firmware mode */}
        {mode === 'firmware' && stores.length > 0 && firmwareSummary.total > 0 && (
          <div className="firmware-summary-bar">
            <div className="firmware-summary-title">Firmware Version Summary:</div>
            <div className="firmware-summary-chips">
              {firmwareSummary.sortedVersions.map(([version, count]) => (
                <div key={version} className="firmware-chip">
                  <span className="firmware-version">{version}</span>
                  <span className="firmware-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Loading Modal Overlay */}
      {loading && progress.total > 0 && (
        <div className="loading-modal-overlay">
          <div className="loading-modal-content">
            <div className="loading-spinner"></div>
            <div className="loading-message">{progress.stage}</div>
            <div className="loading-progress-bar">
              <div
                className="loading-progress-fill"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              />
            </div>
            <div className="loading-stats">
              {progress.current} / {progress.total}
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {/* No Results Message */}
      {stores.length > 0 && processedData.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '16px' }}>
          <p>No stores match the current filter criteria.</p>
          <p style={{ marginTop: '10px', fontSize: '14px' }}>Try changing the filter settings above.</p>
        </div>
      )}

      {/* Results Table */}
      {processedData.length > 0 && (
        <div className="table-section">
          <div className="table-wrapper">
            <table className="gateways-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedStores.size === processedData.length && processedData.length > 0}
                      onChange={toggleAllStores}
                      title="Select all stores"
                    />
                  </th>
                  <th style={{ width: '50px' }}></th>
                  <th onClick={() => handleSort('store')}>
                    Store Code <SortIndicator field="store" />
                  </th>
                  <th onClick={() => handleSort('storeName')}>
                    Store Name <SortIndicator field="storeName" />
                  </th>
                  <th onClick={() => handleSort('totalGateways')}>
                    Total Gateways <SortIndicator field="totalGateways" />
                  </th>
                  {mode === 'analysis' && (
                    <>
                      <th onClick={() => handleSort('onlineGateways')}>
                        Online <SortIndicator field="onlineGateways" />
                      </th>
                      <th onClick={() => handleSort('offlineGateways')}>
                        Offline <SortIndicator field="offlineGateways" />
                      </th>
                    </>
                  )}
                  {mode === 'firmware' ? (
                    <th>Firmware Versions</th>
                  ) : (
                    <th onClick={() => handleSort('uptimePercent')}>
                      Uptime % over {getUptimeTimeRangeLabel()} (Online/Total hours)
                      <SortIndicator field="uptimePercent" />
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((store) => (
                  <React.Fragment key={store.store}>
                    <tr
                      className={`store-row ${expandedStores.has(store.store) ? 'expanded' : ''} ${selectedStores.has(store.store) ? 'selected' : ''}`}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedStores.has(store.store)}
                          onChange={() => toggleStoreSelection(store.store)}
                        />
                      </td>
                      <td className="expand-cell" onClick={() => toggleStoreExpansion(store.store)}>
                        <span className="expand-icon">
                          {expandedStores.has(store.store) ? '▼' : '▶'}
                        </span>
                      </td>
                      <td>{store.store}</td>
                      <td>{store.storeName}</td>
                      <td>{store.totalGateways}</td>
                      {mode === 'analysis' && (
                        <>
                          <td className="online-count">{store.onlineGateways}</td>
                          <td className={store.offlineGateways > 0 ? 'offline-count' : ''}>
                            {store.offlineGateways}
                          </td>
                        </>
                      )}
                      {mode === 'firmware' ? (
                        <td className="firmware-versions-cell">
                          {store.totalGateways > 0 ? (
                            <div className="firmware-mini-chips">
                              {getStoreFirmwareBreakdown(store.store).map(([version, gateways]) => (
                                <span key={version} className="firmware-mini-chip">
                                  <span className="fw-version">{version}</span>
                                  <span className="fw-count">{gateways.length}</span>
                                </span>
                              ))}
                            </div>
                          ) : '-'}
                        </td>
                      ) : (
                        <td
                          className={`${store.uptimePercent >= 95 ? 'uptime-good' : store.uptimePercent >= 80 ? 'uptime-warning' : 'uptime-bad'} uptime-clickable`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (store.totalGateways > 0) {
                              setStoreUptimeModal({
                                company: company,
                                store: store.store,
                                storeName: store.storeName,
                                startDate: startDate,
                                endDate: endDate,
                                mode: mode
                              });
                            }
                          }}
                          title={store.totalGateways > 0 ? "Click to view uptime timeline" : ""}
                        >
                          {store.totalGateways > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', whiteSpace: 'nowrap' }}>
                              {store.hoursOnline !== undefined && (
                                <>
                                  <span style={{ color: '#10b981', fontSize: '18px', fontWeight: '600' }}>{store.hoursOnline}h</span>
                                  <span style={{ color: '#ffffff', fontSize: '18px', fontWeight: '500' }}>/</span>
                                  <span style={{ color: '#60a5fa', fontSize: '18px', fontWeight: '600' }}>{(store.hoursOnline + store.hoursOffline).toFixed(1)}h</span>
                                  <span style={{ color: '#ffffff', fontSize: '18px', fontWeight: '500' }}>=</span>
                                </>
                              )}
                              <span style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: store.uptimePercent >= 95 ? '#34d399' : store.uptimePercent >= 80 ? '#fbbf24' : '#f87171'
                              }}>
                                {store.uptimePercent === 100 ? '100%' : `${store.uptimePercent.toFixed(3)}%`}
                              </span>
                            </div>
                          ) : '-'}
                        </td>
                      )}
                    </tr>
                    {expandedStores.has(store.store) && (
                      <tr className="gateway-details-row">
                        <td colSpan="8">
                          <div className="gateway-details">
                            {mode === 'rebate' ? (
                              // Rebate mode: Show inline daily grid
                              renderInlineDailyGrid(store.store)
                            ) : mode === 'firmware' ? (
                              // Firmware mode: Show gateways grouped by firmware version
                              store.gateways && store.gateways.length > 0 ? (
                                <div className="firmware-breakdown">
                                  {getStoreFirmwareBreakdown(store.store).map(([version, gateways]) => (
                                    <div key={version} className="firmware-group">
                                      <div className="firmware-group-header">
                                        <span className="firmware-group-version">{version}</span>
                                        <span className="firmware-group-count">{gateways.length} gateway{gateways.length !== 1 ? 's' : ''}</span>
                                      </div>
                                      <table className="firmware-gateways-table">
                                        <thead>
                                          <tr>
                                            <th>Gateway Name</th>
                                            <th>MAC Address</th>
                                            <th>Status</th>
                                            <th>Model</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {gateways.map((gw, idx) => (
                                            <tr key={idx} className={gw.networkStatus === 'DISCONNECTED' ? 'offline-gateway' : ''}>
                                              <td>{(gw.name || 'N/A').replace(/(?:BPS|WFC|WLAB|TEST)$/, '')}</td>
                                              <td className="mono">{(gw.macAddress || 'N/A').replace(/(?:BPS|WFC|WLAB|TEST)$/, '')}</td>
                                              <td>
                                                <span className={`status-badge ${gw.networkStatus === 'DISCONNECTED' ? 'status-offline' : 'status-online'}`}>
                                                  {gw.networkStatus || 'UNKNOWN'}
                                                </span>
                                              </td>
                                              <td>{gw.model || 'N/A'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="no-gateways">No gateway data available</div>
                              )
                            ) : (
                              // Analysis mode: Show gateway status table
                              store.gateways && store.gateways.length > 0 ? (
                              <table className="gateways-detail-table">
                                <thead>
                                  <tr>
                                    <th>Gateway Name</th>
                                    <th>MAC Address</th>
                                    <th>Status</th>
                                    <th>Uptime %</th>
                                    <th>Version</th>
                                    <th>Last Connection</th>
                                    <th>Last Reboot</th>
                                    <th>Labels</th>
                                    <th>Model</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {store.gateways.map((gw, idx) => {
                                    // Use actual uptime percentage from MongoDB, fallback to simple calculation
                                    const gwUptimePercent = gw.uptimePercent !== undefined ? gw.uptimePercent : (gw.networkStatus === 'CONNECTED' ? 100 : 0);
                                    return (
                                      <tr
                                        key={idx}
                                        className={`${gw.networkStatus === 'DISCONNECTED' ? 'offline-gateway' : ''} gateway-row-clickable`}
                                        onClick={() => handleGatewayDrillDown(gw)}
                                        title="Click to view detailed uptime history"
                                      >
                                        <td>{(gw.name || 'N/A').replace(/(?:BPS|WFC|WLAB|TEST)$/, '')}</td>
                                        <td className="mono">{(gw.macAddress || 'N/A').replace(/(?:BPS|WFC|WLAB|TEST)$/, '')}</td>
                                        <td>
                                          <span className={`status-badge ${gw.networkStatus === 'DISCONNECTED' ? 'status-offline' : 'status-online'}`}>
                                            {gw.networkStatus || 'UNKNOWN'}
                                          </span>
                                        </td>
                                        <td className={gwUptimePercent >= 95 ? 'uptime-good' : gwUptimePercent >= 80 ? 'uptime-warning' : 'uptime-bad'}>
                                          {gwUptimePercent.toFixed(2)}%
                                        </td>
                                        <td>{gw.version || 'N/A'}</td>
                                        <td className="date-cell">
                                          {gw.lastConnectionDate || 'N/A'}
                                        </td>
                                        <td className="date-cell">
                                          {gw.lastRebootTime || 'N/A'}
                                        </td>
                                        <td>{gw.labelCount || 0}</td>
                                        <td>{gw.model || 'N/A'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              ) : (
                                <div className="no-gateways">No gateway data available</div>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-section">
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  className="pagination-button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {/* Page numbers */}
                <div className="page-numbers">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => goToPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  className="pagination-button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <button
                  className="pagination-button"
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>

              <div className="page-size-selector">
                <label>Rows per page:</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="page-size-select"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State - Only show when NO data has been fetched */}
      {!loading && stores.length === 0 && !error && (
        <div className="empty-state">
          {company ? (
            <p>Company selected: <strong>{company}</strong>. Click "Fetch" to load gateway data.</p>
          ) : (
            <p>Enter a Company Code above to fetch gateway data.</p>
          )}
        </div>
      )}

      {/* AI Analysis Modal */}
      {showAiModal && (
        <div className="ai-modal-overlay" onClick={() => !aiAnalyzing && setShowAiModal(false)}>
          <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-header">
              <h2>Intelligent Gateway Analysis</h2>
              <button
                className="ai-modal-close"
                onClick={() => setShowAiModal(false)}
                disabled={aiAnalyzing}
              >
                ✕
              </button>
            </div>
            <div className="ai-modal-content-wrapper">
              {/* Metrics Sidebar */}
              <div className="ai-modal-sidebar">
                <h3>Analysis Summary</h3>
                <div className="ai-metric">
                  <span className="ai-metric-label">Company:</span>
                  <span className="ai-metric-value">{selectedCompanyObj?.companyName || company}</span>
                </div>
                <div className="ai-metric">
                  <span className="ai-metric-label">Company Code:</span>
                  <span className="ai-metric-value">{company}</span>
                </div>
                <div className="ai-metric">
                  <span className="ai-metric-label">Analyzed Stores:</span>
                  <span className="ai-metric-value">{selectedStores.size > 0 ? selectedStores.size : processedData.length}</span>
                </div>
                <div className="ai-metric">
                  <span className="ai-metric-label">Total Stores:</span>
                  <span className="ai-metric-value">{stores.length}</span>
                </div>
                <div className="ai-metric">
                  <span className="ai-metric-label">Offline Gateways:</span>
                  <span className="ai-metric-value offline">{processedData.reduce((sum, s) => sum + (s.offlineGateways || 0), 0)}</span>
                </div>
                <div className="ai-metric">
                  <span className="ai-metric-label">Total Gateways:</span>
                  <span className="ai-metric-value">{processedData.reduce((sum, s) => sum + (s.totalGateways || 0), 0)}</span>
                </div>
              </div>

              {/* Main Content */}
              <div className="ai-modal-body">
                {aiAnalyzing && (
                  <div className="ai-analyzing">
                    <div className="ai-spinner"></div>
                    <p>Analyzing gateway data...</p>
                    <p className="ai-analyzing-note">
                      Examining network patterns, downtime, IP configurations, and more.
                    </p>
                  </div>
                )}
                {aiError && (
                  <div className="ai-error">
                    <strong>Error:</strong> {aiError}
                  </div>
                )}
                {aiResult && (
                  <div className="ai-result">
                    <div dangerouslySetInnerHTML={{ __html: formatMarkdown(aiResult) }} />
                  </div>
                )}
              </div>
            </div>
            {!aiAnalyzing && (aiResult || aiError) && (
              <div className="ai-modal-footer">
                {aiResult && (
                  <button
                    className="ai-download-button"
                    onClick={() => {
                      const blob = new Blob([aiResult], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `gateway_analysis_${company}_${new Date().toISOString().split('T')[0]}.txt`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Download Analysis
                  </button>
                )}
                <button className="ai-close-button" onClick={() => setShowAiModal(false)}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gateway Drill-Down Modal */}
      {drillDownGateway && (
        <div className="ai-modal-overlay" onClick={() => setDrillDownGateway(null)}>
          <div className="ai-modal-content drill-down-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-header">
              <h2>Gateway Uptime Details</h2>
              <button className="ai-close-button-x" onClick={() => setDrillDownGateway(null)}>
                ✕
              </button>
            </div>
            <div className="ai-modal-body">
              {/* Gateway Info */}
              <div className="drill-down-gateway-info">
                <h3>{drillDownGateway.name || 'Unknown Gateway'}</h3>
                <div className="gateway-info-grid">
                  <div><strong>MAC Address:</strong> {drillDownGateway.macAddress || 'N/A'}</div>
                  <div><strong>Device ID:</strong> {drillDownGateway.deviceId || 'N/A'}</div>
                  <div><strong>Status:</strong> <span className={`status-badge ${drillDownGateway.networkStatus === 'DISCONNECTED' ? 'status-offline' : 'status-online'}`}>
                    {drillDownGateway.networkStatus || 'UNKNOWN'}
                  </span></div>
                  <div><strong>Version:</strong> {drillDownGateway.version || 'N/A'}</div>
                </div>
              </div>

              {drillDownLoading && (
                <div className="loading-message">Loading uptime data...</div>
              )}

              {drillDownError && (
                <div className="error-message">{drillDownError}</div>
              )}

              {drillDownData && drillDownData.summary && (
                <>
                  {/* Summary Stats */}
                  <div className="drill-down-summary">
                    <h4>Uptime Summary
                      {(startDate || endDate) && (
                        <span className="date-range-label">
                          {' '}({startDate ? new Date(startDate).toLocaleDateString() : 'Start'} - {endDate ? new Date(endDate).toLocaleDateString() : 'Now'})
                        </span>
                      )}
                    </h4>
                    <div className="summary-stats-grid">
                      <div className="summary-stat">
                        <div className="stat-label">Uptime</div>
                        <div className={`stat-value ${drillDownData.summary.uptimePercent >= 95 ? 'uptime-good' : drillDownData.summary.uptimePercent >= 80 ? 'uptime-warning' : 'uptime-bad'}`}>
                          {drillDownData.summary.uptimePercent.toFixed(1)}%
                        </div>
                        <div className="stat-detail">{drillDownData.summary.uptimeHours.toFixed(1)}h online</div>
                      </div>
                      <div className="summary-stat">
                        <div className="stat-label">Downtime</div>
                        <div className="stat-value uptime-bad">
                          {(100 - drillDownData.summary.uptimePercent).toFixed(1)}%
                        </div>
                        <div className="stat-detail">{drillDownData.summary.downtimeHours.toFixed(1)}h offline</div>
                      </div>
                      <div className="summary-stat">
                        <div className="stat-label">Snapshots</div>
                        <div className="stat-value">{drillDownData.summary.totalSnapshots}</div>
                        <div className="stat-detail">data points</div>
                      </div>
                    </div>
                  </div>

                  {/* Snapshot History */}
                  {drillDownData.summary.snapshots && drillDownData.summary.snapshots.length > 0 && (
                    <div className="drill-down-snapshots">
                      <h4>Snapshot History (Most Recent)</h4>
                      <div className="snapshots-table-container">
                        <table className="snapshots-table">
                          <thead>
                            <tr>
                              <th>Timestamp</th>
                              <th>Status</th>
                              <th>Uptime %</th>
                              <th>Uptime Hours</th>
                              <th>Downtime Hours</th>
                            </tr>
                          </thead>
                          <tbody>
                            {drillDownData.summary.snapshots.slice(0, 10).map((snapshot, idx) => (
                              <tr key={idx}>
                                <td>{new Date(snapshot.timestamp).toLocaleString()}</td>
                                <td>
                                  <span className={`status-badge ${snapshot.status === 'DISCONNECTED' ? 'status-offline' : 'status-online'}`}>
                                    {snapshot.status}
                                  </span>
                                </td>
                                <td className={snapshot.uptimePercent >= 95 ? 'uptime-good' : snapshot.uptimePercent >= 80 ? 'uptime-warning' : 'uptime-bad'}>
                                  {snapshot.uptimePercent.toFixed(1)}%
                                </td>
                                <td>{snapshot.uptimeHours.toFixed(1)}h</td>
                                <td>{snapshot.downtimeHours.toFixed(1)}h</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 5-Minute Intervals (if available) */}
                  {drillDownData.intervals && drillDownData.intervals.periods && drillDownData.intervals.periods.length > 0 && (
                    <div className="drill-down-intervals">
                      <h4>Detailed 5-Minute Intervals</h4>
                      <p className="intervals-note">Showing most recent period with {drillDownData.intervals.periods[0].totalIntervals} intervals</p>
                      <div className="intervals-grid">
                        {drillDownData.intervals.periods[0].intervals.slice(0, 50).map((interval, idx) => (
                          <div
                            key={idx}
                            className={`interval-block ${interval.status === 'online' ? 'interval-online' : 'interval-offline'}`}
                            title={`${interval.timeInterval} - ${interval.status} (${interval.count} heartbeats)`}
                          >
                            {new Date(interval.timeInterval.replace(' ', 'T')).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </div>
                        ))}
                      </div>
                      {drillDownData.intervals.periods[0].intervals.length > 50 && (
                        <p className="intervals-note">+ {drillDownData.intervals.periods[0].intervals.length - 50} more intervals</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="ai-modal-footer">
              <button className="ai-close-button" onClick={() => setDrillDownGateway(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Store Uptime Timeline Modal */}
      {storeUptimeModal && (
        <StoreUptimeModal
          company={storeUptimeModal.company}
          store={storeUptimeModal.store}
          storeName={storeUptimeModal.storeName}
          startDate={storeUptimeModal.startDate}
          endDate={storeUptimeModal.endDate}
          mode={storeUptimeModal.mode}
          onClose={() => setStoreUptimeModal(null)}
        />
      )}

      {/* Gateway Day Drill-Down Modal */}
      {gatewayDayModal && (
        <GatewayDayModal
          company={gatewayDayModal.company}
          store={gatewayDayModal.store}
          gateway={gatewayDayModal.gateway}
          gatewayName={gatewayDayModal.gatewayName}
          day={gatewayDayModal.day}
          onClose={() => setGatewayDayModal(null)}
        />
      )}

      {/* Offline Gateways Timeline Modal */}
      {showOfflineModal && (
        <div className="ai-modal-overlay" onClick={() => setShowOfflineModal(false)}>
          <div className="ai-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', maxHeight: '90vh' }}>
            <div className="ai-modal-header">
              <h2>Downtime Timeline</h2>
              <button
                className="ai-modal-close"
                onClick={() => setShowOfflineModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="ai-modal-body" style={{ maxHeight: '70vh', overflow: 'auto', padding: '20px' }}>
              {loadingTimeline ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#e0e0e0' }}>
                  <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
                  <div>Loading downtime timeline...</div>
                </div>
              ) : downtimeTimeline?.error ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Error</div>
                  <div>{downtimeTimeline.error}</div>
                </div>
              ) : downtimeTimeline?.timeline && downtimeTimeline.timeline.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#e0e0e0' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Perfect Uptime!</div>
                  <div>All gateways had 100% uptime during this period</div>
                </div>
              ) : downtimeTimeline?.timeline ? (
                <>
                  {/* Summary Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ background: '#1e2837', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', color: '#ffffff', marginBottom: '8px' }}>Days with Downtime</div>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f87171' }}>{downtimeTimeline.totalDaysWithDowntime}</div>
                    </div>
                    <div style={{ background: '#1e2837', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', color: '#ffffff', marginBottom: '8px' }}>Total Downtime Events</div>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#fbbf24' }}>{downtimeTimeline.totalDowntimeEvents}</div>
                    </div>
                    <div style={{ background: '#1e2837', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', color: '#ffffff', marginBottom: '8px' }}>Average Events/Day</div>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#60a5fa' }}>
                        {downtimeTimeline.totalDaysWithDowntime > 0 ? Math.round(downtimeTimeline.totalDowntimeEvents / downtimeTimeline.totalDaysWithDowntime) : 0}
                      </div>
                    </div>
                  </div>

                  {/* Timeline Chart */}
                  <div style={{ background: '#0f1419', padding: '20px', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: '20px', color: '#e0e0e0' }}>Downtime Events by Day</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {downtimeTimeline.timeline.map((day, idx) => {
                        const maxEvents = Math.max(...downtimeTimeline.timeline.map(d => d.eventCount));
                        const barWidth = (day.eventCount / maxEvents) * 100;

                        const isExpanded = expandedDays.has(day.day);

                        return (
                          <div key={idx}>
                            <div
                              style={{ display: 'flex', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}
                              onMouseEnter={(e) => {
                                setHoveredDay(day.day);
                                setMousePos({ x: e.clientX, y: e.clientY });
                              }}
                              onMouseMove={(e) => {
                                if (hoveredDay === day.day) {
                                  setMousePos({ x: e.clientX, y: e.clientY });
                                }
                              }}
                              onMouseLeave={() => setHoveredDay(null)}
                              onClick={() => {
                                const newExpanded = new Set(expandedDays);
                                if (isExpanded) {
                                  newExpanded.delete(day.day);
                                } else {
                                  newExpanded.add(day.day);
                                }
                                setExpandedDays(newExpanded);
                              }}
                            >
                              {/* Date Label */}
                              <div style={{ width: '120px', fontSize: '14px', color: '#ffffff', fontWeight: '500' }}>
                                {new Date(day.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>

                              {/* Bar Chart */}
                              <div style={{ flex: 1, position: 'relative' }}>
                                <div
                                  style={{
                                    height: '32px',
                                    width: `${barWidth}%`,
                                    background: `linear-gradient(90deg, ${day.eventCount > 50 ? '#ef4444' : day.eventCount > 20 ? '#f59e0b' : '#f87171'} 0%, ${day.eventCount > 50 ? '#dc2626' : day.eventCount > 20 ? '#d97706' : '#ef4444'} 100%)`,
                                    borderRadius: '4px',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0 12px',
                                    minWidth: '60px',
                                    boxShadow: hoveredDay === day.day ? '0 4px 12px rgba(239, 68, 68, 0.5)' : 'none',
                                    transform: hoveredDay === day.day ? 'scale(1.02)' : 'scale(1)'
                                  }}
                                >
                                  <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#fff' }}>
                                    {day.eventCount} events
                                  </span>
                                </div>
                              </div>

                              {/* Event Count */}
                              <div style={{ width: '100px', textAlign: 'right', fontSize: '14px', color: '#ffffff', fontWeight: '600' }}>
                                {day.uniqueGateways} GW{day.uniqueGateways !== 1 ? 's' : ''}
                              </div>
                            </div>

                            {/* Expanded Events Table */}
                            {isExpanded && day.events && day.events.length > 0 && (
                              <div style={{
                                marginLeft: '120px',
                                marginTop: '8px',
                                marginBottom: '12px',
                                background: '#1e2837',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                border: '1px solid #374151'
                              }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: '#111827', borderBottom: '2px solid #374151' }}>
                                      <th style={{ padding: '10px', textAlign: 'left', color: '#ffffff', fontSize: '12px', fontWeight: '600' }}>Store</th>
                                      <th style={{ padding: '10px', textAlign: 'left', color: '#ffffff', fontSize: '12px', fontWeight: '600' }}>Gateway</th>
                                      <th style={{ padding: '10px', textAlign: 'left', color: '#ffffff', fontSize: '12px', fontWeight: '600' }}>Time</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(() => {
                                      // Group events by store and sort
                                      const eventsByStore = {};
                                      day.events.forEach(event => {
                                        if (!eventsByStore[event.storeId]) {
                                          eventsByStore[event.storeId] = {
                                            storeName: event.storeName,
                                            storeId: event.storeId,
                                            events: []
                                          };
                                        }
                                        eventsByStore[event.storeId].events.push(event);
                                      });

                                      // Sort events within each store by timestamp
                                      Object.values(eventsByStore).forEach(store => {
                                        store.events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                                      });

                                      // Render grouped by store
                                      let rowIndex = 0;
                                      return Object.values(eventsByStore).sort((a, b) => a.storeId.localeCompare(b.storeId)).map((store, storeIdx) => (
                                        store.events.map((event, eventIdx) => {
                                          const isLastRow = rowIndex === day.events.length - 1;
                                          rowIndex++;
                                          return (
                                            <tr key={`${storeIdx}-${eventIdx}`} style={{
                                              borderBottom: !isLastRow ? '1px solid #374151' : 'none',
                                              transition: 'background 0.15s ease'
                                            }}>
                                              <td style={{ padding: '8px 10px', color: '#ffffff', fontSize: '12px' }}>
                                                {event.storeId} - {event.storeName}
                                              </td>
                                              <td style={{ padding: '8px 10px', color: '#ffffff', fontSize: '12px', fontFamily: 'monospace' }}>
                                                {event.gwName.replace(/(?:BPS|WFC|WLAB|CDL|AMZ|GWM|LOWM|TBS|TEL|SPH|WYF)$/i, '')}
                                              </td>
                                              <td style={{ padding: '8px 10px', color: '#d1d5db', fontSize: '12px' }}>
                                                {new Date(event.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                              </td>
                                            </tr>
                                          );
                                        })
                                      ));
                                    })()}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Hover Tooltip - 2x size, positioned to the side of cursor */}
                    {hoveredDay && downtimeTimeline.timeline.find(d => d.day === hoveredDay) && (() => {
                      const tooltipWidth = 400;
                      const tooltipHeight = 300; // Estimated max height
                      const offset = 20; // Distance from cursor

                      // Calculate position - prefer right side, fall back to left if needed
                      let left = mousePos.x + offset;
                      let top = mousePos.y;

                      // Check if tooltip goes off right edge
                      if (left + tooltipWidth > window.innerWidth) {
                        left = mousePos.x - tooltipWidth - offset; // Position to the left instead
                      }

                      // Check if tooltip goes off bottom edge
                      if (top + tooltipHeight > window.innerHeight) {
                        top = window.innerHeight - tooltipHeight - 20;
                      }

                      // Check if tooltip goes off top edge
                      if (top < 20) {
                        top = 20;
                      }

                      return (
                        <div style={{
                          position: 'fixed',
                          left: `${left}px`,
                          top: `${top}px`,
                          background: '#1e2837',
                          border: '2px solid #f87171',
                          borderRadius: '12px',
                          padding: '24px',
                          zIndex: 10000,
                          minWidth: '400px',
                          maxWidth: '450px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                          pointerEvents: 'none'
                        }}>
                        {(() => {
                          const day = downtimeTimeline.timeline.find(d => d.day === hoveredDay);
                          return (
                            <>
                              <div style={{ fontSize: '16px', color: '#ffffff', marginBottom: '8px', fontWeight: '600' }}>
                                {new Date(day.day).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                              </div>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f87171', marginBottom: '12px' }}>
                                {day.eventCount} downtime events
                              </div>
                              <div style={{ fontSize: '16px', color: '#ffffff', marginBottom: '4px' }}>
                                {day.uniqueGateways} gateway{day.uniqueGateways !== 1 ? 's' : ''} affected
                              </div>
                              <div style={{ fontSize: '16px', color: '#ffffff' }}>
                                {day.uniqueStores} store{day.uniqueStores !== 1 ? 's' : ''} affected
                              </div>
                              {day.events && day.events.length > 0 && (
                                <div style={{ marginTop: '12px', fontSize: '14px', color: '#ffffff', maxHeight: '200px', overflow: 'auto' }}>
                                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Top Events:</div>
                                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px', fontStyle: 'italic' }}>
                                    Store - Gateway - Time on {new Date(day.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </div>
                                  {day.events.slice(0, 5).map((event, i) => (
                                    <div key={i} style={{ padding: '4px 0', borderBottom: i < 4 ? '1px solid #374151' : 'none' }}>
                                      {event.storeName} - {event.gwName.replace(/(?:BPS|WFC|WLAB|CDL|AMZ|GWM|LOWM|TBS|TEL|SPH|WYF)$/i, '')} - {new Date(event.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                    </div>
                                  ))}
                                  {day.events.length > 5 && (
                                    <div style={{ marginTop: '8px', fontStyle: 'italic', fontSize: '13px' }}>
                                      +{day.events.length - 5} more...
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        </div>
                      );
                    })()}
                  </div>
                </>
              ) : null}
            </div>
            <div className="ai-modal-footer">
              <button className="ai-close-button" onClick={() => setShowOfflineModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Configuration Modal */}
      {showEmailModal && (
        <div className="email-config-modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="email-config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-modal-header">
              <h2>Email Report Configuration</h2>
              <button className="modal-close" onClick={() => setShowEmailModal(false)}>×</button>
            </div>

            <div className="email-modal-body">
              {loadingEmailConfig ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading configuration...</p>
                </div>
              ) : emailConfig ? (
                <>
                  {/* Enable/Disable Toggle */}
                  <div className="config-section">
                    <div className="config-header">
                      <h3>Email Reports</h3>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={emailConfig.enabled}
                          onChange={handleToggleEnabled}
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-label">
                          {emailConfig.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </div>
                    <p className="config-description">
                      Automatically send monthly uptime reports to configured recipients.
                      {emailConfig.lastSent?.date && (
                        <span className="last-sent">
                          Last sent: {new Date(emailConfig.lastSent.date).toLocaleString('en-US', { timeZone: 'America/New_York' })} EST
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Include HTML Report Toggle */}
                  <div className="config-section">
                    <div className="config-header">
                      <h3>Include Standalone HTML Report</h3>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={emailConfig.reportConfig?.includeHtmlReport ?? false}
                          onChange={handleToggleHtmlReport}
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-label">
                          {emailConfig.reportConfig?.includeHtmlReport ? 'Included' : 'Not Included'}
                        </span>
                      </label>
                    </div>
                    <p className="config-description">
                      Include a link to a standalone HTML report in the email. The email will always include a text summary table.
                      <span className="warning-note">
                        Note: HTML report currently has browser compatibility issues. Recommend keeping this off until resolved.
                      </span>
                    </p>
                  </div>

                  {/* Recipients List */}
                  <div className="config-section">
                    <h3>Recipients ({emailConfig.recipients.length})</h3>
                    <div className="recipients-list">
                      {emailConfig.recipients.map((recipient, idx) => (
                        <div key={idx} className="recipient-item">
                          <div className="recipient-info">
                            <span className="recipient-email">{recipient.email}</span>
                            {recipient.name !== recipient.email && (
                              <span className="recipient-name">({recipient.name})</span>
                            )}
                          </div>
                          <button
                            className="btn-remove"
                            onClick={() => handleRemoveRecipient(recipient.email)}
                            title="Remove recipient"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {emailConfig.recipients.length === 0 && (
                        <p className="no-recipients">No recipients configured</p>
                      )}
                    </div>

                    {/* Add Recipient Form */}
                    <div className="add-recipient-form">
                      <input
                        type="email"
                        placeholder="Email address"
                        value={newRecipientEmail}
                        onChange={(e) => setNewRecipientEmail(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddRecipient()}
                      />
                      <input
                        type="text"
                        placeholder="Name (optional)"
                        value={newRecipientName}
                        onChange={(e) => setNewRecipientName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddRecipient()}
                      />
                      <button
                        className="btn-add"
                        onClick={handleAddRecipient}
                        disabled={!newRecipientEmail}
                      >
                        Add Recipient
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            <div className="modal-footer">
              <button
                className="btn-test"
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail || !emailConfig || emailConfig.recipients.length === 0}
              >
                {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
              </button>
              <button className="btn-close" onClick={() => setShowEmailModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ProtectedComponent>
  );
}

// Enhanced markdown formatter with table support for AI results
function formatMarkdown(markdown) {
  if (!markdown) return '';

  // Process tables first (before other replacements)
  let html = markdown;

  // Detect text-based data tables (pipe-separated or fixed-width)
  const lines = html.split('\n');
  let processedLines = [];
  let inTable = false;
  let tableRows = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Detect table separator lines (dashes)
    const isSeparator = /^[-=]+$/.test(line) || /^[-|\s]+$/.test(line);

    // Detect table headers (lines with | delimiters or multiple spaces)
    const isPipeSeparated = line.includes('|') && line.split('|').length > 2;
    const hasMultipleSpaces = /\s{2,}/.test(line) && line.length > 20;

    if (isPipeSeparated || isSeparator || (hasMultipleSpaces && !line.startsWith('#') && line.length > 0)) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      tableRows.push(line);
    } else {
      if (inTable && tableRows.length > 1) {
        // Process the accumulated table
        const tableHtml = convertTextTableToHTML(tableRows);
        processedLines.push(tableHtml);
        tableRows = [];
        inTable = false;
      }
      if (!inTable) {
        processedLines.push(line);
      }
    }
  }

  // Process any remaining table
  if (inTable && tableRows.length > 1) {
    const tableHtml = convertTextTableToHTML(tableRows);
    processedLines.push(tableHtml);
  }

  html = processedLines.join('\n');

  // Continue with standard markdown processing
  html = html
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^\d+\.\s+(.*$)/gim, '<li>$1</li>')
    .replace(/^-\s+(.*$)/gim, '<li>$1</li>')
    // Line breaks
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br />');

  // Wrap lists
  html = html.replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>');

  // Wrap paragraphs (but not tables or headers)
  html = '<p>' + html + '</p>';
  html = html.replace(/<p>(<table[\s\S]*?<\/table>)<\/p>/g, '$1');
  html = html.replace(/<p>(<h[1-4]>[\s\S]*?<\/h[1-4]>)<\/p>/g, '$1');

  return html;
}

// Convert text-based table to HTML table
function convertTextTableToHTML(rows) {
  if (rows.length < 2) return rows.join('\n');

  // Detect if pipe-separated or space-separated
  const isPipeSeparated = rows[0].includes('|');

  let headers = [];
  let dataRows = [];

  if (isPipeSeparated) {
    // Parse pipe-separated table
    headers = rows[0].split('|').map(h => h.trim()).filter(h => h);

    // Skip separator row if exists (----)
    const startIdx = rows[1].includes('---') || rows[1].includes('===') ? 2 : 1;

    for (let i = startIdx; i < rows.length; i++) {
      const cells = rows[i].split('|').map(c => c.trim()).filter(c => c);
      if (cells.length > 0) {
        dataRows.push(cells);
      }
    }
  } else {
    // Parse space-separated table (fixed-width)
    // First row is header
    headers = rows[0].split(/\s{2,}/).map(h => h.trim()).filter(h => h);

    // Skip separator row if exists
    const startIdx = (rows[1] && (rows[1].includes('---') || rows[1].includes('==='))) ? 2 : 1;

    for (let i = startIdx; i < rows.length; i++) {
      const cells = rows[i].split(/\s{2,}/).map(c => c.trim()).filter(c => c);
      if (cells.length > 0 && !cells[0].startsWith('---')) {
        dataRows.push(cells);
      }
    }
  }

  if (headers.length === 0 || dataRows.length === 0) {
    return rows.join('\n');
  }

  // Build HTML table
  let tableHtml = '<table>';
  tableHtml += '<thead><tr>';
  headers.forEach(header => {
    tableHtml += `<th>${header}</th>`;
  });
  tableHtml += '</tr></thead>';

  tableHtml += '<tbody>';
  dataRows.forEach(row => {
    tableHtml += '<tr>';
    row.forEach(cell => {
      tableHtml += `<td>${cell}</td>`;
    });
    tableHtml += '</tr>';
  });
  tableHtml += '</tbody>';
  tableHtml += '</table>';

  return tableHtml;
}

export default OfflineGatewayAnalysis;
